import * as THREE from 'three';
import { Player } from './Player';
import { Enemy } from './Enemy';
import { NetworkManager } from './Network';
import { EndingSequence } from './Ending';
import { EffectsManager } from './Effects';

export class Game {
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private player!: Player;
  private enemies: Enemy[] = [];
  private network!: NetworkManager;
  private ending!: EndingSequence;
  private effects!: EffectsManager;
  private clock!: THREE.Clock;
  
  private isRunning = false;
  private wave = 1;
  private playersOnline = 1;
  
  private keys = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    jump: false,
    crouch: false,
    attack: false
  };
  
  async init(): Promise<void> {
    this.setupRenderer();
    this.setupScene();
    this.setupLighting();
    this.setupEnvironment();
    this.setupEffects();
    this.network = new NetworkManager();
    this.setupPlayer();
    this.setupEventListeners();
    this.setupUI();
    
    this.isRunning = true;
    this.animate();
  }
  
  private setupRenderer(): void {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    
    const container = document.getElementById('game-container');
    if (container) {
      container.appendChild(this.renderer.domElement);
    }
  }
  
  private setupScene(): void {
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0a0a0a, 0.015);
    
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 5, 10);
    
    this.clock = new THREE.Clock();
  }
  
  private setupLighting(): void {
    const ambient = new THREE.AmbientLight(0x222244, 0.3);
    this.scene.add(ambient);
    
    const main = new THREE.DirectionalLight(0xffffff, 1);
    main.position.set(50, 100, 50);
    main.castShadow = true;
    main.shadow.mapSize.width = 2048;
    main.shadow.mapSize.height = 2048;
    main.shadow.camera.near = 0.5;
    main.shadow.camera.far = 500;
    main.shadow.camera.left = -50;
    main.shadow.camera.right = 50;
    main.shadow.camera.top = 50;
    main.shadow.camera.bottom = -50;
    this.scene.add(main);
    
    const rim = new THREE.PointLight(0x00ff00, 0.5, 50);
    rim.position.set(-20, 10, -20);
    this.scene.add(rim);
    
    const fill = new THREE.PointLight(0xff0044, 0.3, 30);
    fill.position.set(20, 5, 20);
    this.scene.add(fill);
  }
  
  private setupEnvironment(): void {
    const floorGeo = new THREE.PlaneGeometry(200, 200);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x111122,
      metalness: 0.8,
      roughness: 0.4
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);
    
    const gridHelper = new THREE.GridHelper(200, 50, 0x00ff00, 0x002200);
    gridHelper.position.y = 0.01;
    this.scene.add(gridHelper);
    
    for (let i = 0; i < 15; i++) {
      const size = Math.random() * 3 + 2;
      const boxGeo = new THREE.BoxGeometry(size, size * 2, size);
      const boxMat = new THREE.MeshStandardMaterial({
        color: Math.random() > 0.5 ? 0x222233 : 0x332222,
        metalness: 0.6,
        roughness: 0.3
      });
      const box = new THREE.Mesh(boxGeo, boxMat);
      box.position.set(
        (Math.random() - 0.5) * 100,
        size,
        (Math.random() - 0.5) * 100
      );
      box.castShadow = true;
      box.receiveShadow = true;
      this.scene.add(box);
    }
    
    const skyGeo = new THREE.SphereGeometry(400, 32, 32);
    const skyMat = new THREE.MeshBasicMaterial({
      color: 0x050510,
      side: THREE.BackSide
    });
    const sky = new THREE.Mesh(skyGeo, skyMat);
    this.scene.add(sky);
  }
  
  private setupEffects(): void {
    this.effects = new EffectsManager(this.scene, this.renderer);
    this.effects.setupPostProcessing(this.camera);
    this.effects.setupParticles();
  }
  
  private setupPlayer(): void {
    this.player = new Player(this.scene);
    this.player.setPosition(0, 0, 0);
  }
  
  private setupEventListeners(): void {
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));
    window.addEventListener('resize', () => this.onResize());
  }
  
  private onKeyDown(e: KeyboardEvent): void {
    const key = e.key.toLowerCase();
    switch (key) {
      case 'w': this.keys.forward = true; break;
      case 's': this.keys.backward = true; break;
      case 'a': this.keys.left = true; break;
      case 'd': this.keys.right = true; break;
      case ' ': this.keys.jump = true; break;
      case 'c': this.keys.crouch = true; break;
      case 'b': this.keys.attack = true; break;
    }
  }
  
  private onKeyUp(e: KeyboardEvent): void {
    const key = e.key.toLowerCase();
    switch (key) {
      case 'w': this.keys.forward = false; break;
      case 's': this.keys.backward = false; break;
      case 'a': this.keys.left = false; break;
      case 'd': this.keys.right = false; break;
      case ' ': this.keys.jump = false; break;
      case 'c': this.keys.crouch = false; break;
      case 'b': this.keys.attack = false; break;
    }
  }
  
  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.effects.onResize();
  }
  
  private setupUI(): void {
    const btnCreate = document.getElementById('btn-create');
    const btnJoin = document.getElementById('btn-join');
    const btnConnect = document.getElementById('btn-connect');
    const roomCode = document.getElementById('room-code');
    const status = document.getElementById('status');
    const hud = document.getElementById('hud');
    const menu = document.getElementById('menu');
    
    btnCreate?.addEventListener('click', async () => {
      if (status) status.textContent = 'Creating room...';
      const code = await this.network.createRoom();
      if (roomCode) {
        roomCode.style.display = 'block';
        (roomCode as HTMLInputElement).value = code;
      }
      if (btnJoin) btnJoin.style.display = 'none';
      if (btnCreate) btnCreate.style.display = 'none';
      if (status) status.textContent = `Room code: ${code}`;
      if (menu) menu.classList.add('hidden');
      if (hud) hud.classList.remove('hidden');
      this.startWaves();
    });
    
    btnJoin?.addEventListener('click', () => {
      if (btnCreate) btnCreate.style.display = 'none';
      if (btnJoin) btnJoin.style.display = 'none';
      if (roomCode) {
        roomCode.style.display = 'block';
        (roomCode as HTMLInputElement).placeholder = 'ENTER CODE';
      }
      if (btnConnect) btnConnect.style.display = 'block';
    });
    
    btnConnect?.addEventListener('click', async () => {
      const code = (roomCode as HTMLInputElement)?.value;
      if (code) {
        if (status) status.textContent = 'Connecting...';
        await this.network.joinRoom(code);
        if (status) status.textContent = 'Connected!';
        if (menu) menu.classList.add('hidden');
        if (hud) hud.classList.remove('hidden');
        this.playersOnline++;
        this.updatePlayersUI();
        this.startWaves();
      }
    });
  }
  
  private startWaves(): void {
    this.spawnWave();
  }
  
  private spawnWave(): void {
    const count = 3 + this.wave * 2;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 30 + Math.random() * 20;
      const enemy = new Enemy(this.scene);
      enemy.setPosition(
        Math.cos(angle) * dist,
        0,
        Math.sin(angle) * dist
      );
      this.enemies.push(enemy);
    }
    this.updateWaveUI();
  }
  
  private updateWaveUI(): void {
    const waveEl = document.getElementById('wave');
    if (waveEl) waveEl.textContent = `Wave: ${this.wave}`;
  }
  
  private updatePlayersUI(): void {
    const playersEl = document.getElementById('players-online');
    if (playersEl) playersEl.textContent = `Players: ${this.playersOnline}`;
  }
  
  private updateHealthUI(): void {
    const health = this.player.getHealth();
    const fill = document.getElementById('health-fill');
    const text = document.getElementById('health-text');
    if (fill) fill.style.width = `${health}%`;
    if (text) text.textContent = String(Math.round(health));
  }
  
  private checkCollisions(): void {
    for (const enemy of this.enemies) {
      if (enemy.isDead()) continue;
      
      const dist = this.player.getPosition().distanceTo(enemy.getPosition());
      if (dist < 2) {
        if (this.player.tryAttack()) {
          enemy.takeDamage(25);
          if (enemy.isDead()) {
            this.effects.spawnDeathEffect(enemy.getPosition());
          }
        }
        if (enemy.tryAttack()) {
          this.player.takeDamage(10);
          this.updateHealthUI();
          this.effects.shakeCamera();
        }
      }
    }
  }
  
  private checkWaveComplete(): void {
    const alive = this.enemies.filter(e => !e.isDead()).length;
    if (alive === 0) {
      this.wave++;
      if (this.wave > 5) {
        this.triggerEnding();
      } else {
        setTimeout(() => this.spawnWave(), 2000);
      }
    }
  }
  
  private triggerEnding(): void {
    this.ending = new EndingSequence(this.scene, this.camera);
    this.ending.play(() => {
      setTimeout(() => {
        alert('THE ROT - CLEANUP COMPLETE');
        location.reload();
      }, 1000);
    });
  }
  
  private animate = (): void => {
    if (!this.isRunning) return;
    
    requestAnimationFrame(this.animate);
    
    const delta = this.clock.getDelta();
    
    const moveDir = new THREE.Vector3();
    if (this.keys.forward) moveDir.z -= 1;
    if (this.keys.backward) moveDir.z += 1;
    if (this.keys.left) moveDir.x -= 1;
    if (this.keys.right) moveDir.x += 1;
    
    this.player.move(moveDir, delta);
    this.player.jump(this.keys.jump);
    this.player.crouch(this.keys.crouch);
    this.player.attack(this.keys.attack);
    
    const camPos = this.player.getPosition().clone();
    camPos.y += 3;
    camPos.z += 8;
    this.camera.position.lerp(camPos, 0.1);
    this.camera.lookAt(this.player.getPosition());
    
    for (const enemy of this.enemies) {
      if (!enemy.isDead()) {
        enemy.update(delta, this.player.getPosition());
      }
    }
    
    this.checkCollisions();
    this.checkWaveComplete();
    this.effects.update(delta);
    
    this.renderer.render(this.scene, this.camera);
  };
  
  cleanup(): void {
    this.isRunning = false;
    this.network.cleanup();
  }
}